<template>
  <div class="edit-lyric height-all">
    <textarea
      ref="refLyricTextarea"
      :placeholder="props.placeholder ?? $$t('create.enter_song_lyrics')"
      @click="handleFocus"
      @input="handleInput"
      @blur.native="handleBlur"
      @change="handleChange"
      :value="renderData.lyric"
      :readonly="props.isLoading"
      @compositionstart="handleCompositionStart"
      @compositionend="handleCompositionEnd"
    >
    </textarea>
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
  nextTick,
  onBeforeUnmount,
} from "vue"
import sseGenerateApi from "@/api/api/apiSse.js"

import globalConfig from "@/config/config.js"
const { $$language, $$t, $$tm, $$tValue } = getCurrentInstance().appContext?.config.globalProperties.$i18n ?? {}

const MAX_LYRIC_COUNT = globalConfig.MAX_LYRIC_COUNT

const stateData = {
  cursorPosition: 0,
  beforeText: "",
  afterText: "",
}
const refLyricTextarea = ref(null)
const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  lyric: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: null,
  },
  maxLength: {
    type: Number,
    default: null,
  },
})
const emits = defineEmits([
  "input",
  "change",
  "countChange",
  "input:lyric",
  "blur",
  "focus",
  "cursorPositionChange",
])

const renderData = reactive({
  tagVisible: false,
  tagPositionTop: 0,
  lyric: "",
  count: 0,
  maxLength: props.maxLength ?? MAX_LYRIC_COUNT,
})

const insertTag = (tag) => {
  const tagValue = tag[`label_${$$language()}`]
  let beforeText = ""
  let isAddBreak =
    !stateData.afterText ||
    /^\n{0,}\[[a-zA-Z-]+\]/.test(stateData.afterText ?? "") ||
    /^(?!\n).*/.test(stateData.afterText ?? "")
      ? "\n"
      : ""
  if (stateData.beforeText == "") {
    beforeText = `[${tagValue}]` + isAddBreak
  } else if (stateData.beforeText.endsWith("\n")) {
    beforeText = `${stateData.beforeText}\n[${tagValue}]` + isAddBreak
  } else {
    beforeText = `${stateData.beforeText}\n\n[${tagValue}]` + isAddBreak
  }
  let lyric = stateData.beforeText + stateData.afterText
  if (lyric.length <= renderData.maxLength) {
    stateData.beforeText = beforeText
    stateData.cursorPosition = stateData.beforeText.length
    renderData.lyric = stateData.beforeText + stateData.afterText
    nextTick(() => {
      emits("change", renderData.lyric)
      emits(
        "cursorPositionChange",
        stateData.cursorPosition,
        stateData.beforeText,
        stateData.afterText
      )
    })
  }
}

const calculateChar = (str) => {
  return str.length
  // let tempCount = 0;
  // for (let i = 0; i < str.length; i++) {
  //   const charCode = str.charCodeAt(i);
  //   if (
  //     (charCode >= 32 && charCode <= 47) ||
  //     (charCode >= 58 && charCode <= 64) ||
  //     (charCode >= 91 && charCode <= 96) ||
  //     (charCode >= 123 && charCode <= 126)
  //   ) {
  //     let punctuations = 1;
  //     while (i + punctuations < str.length && punctuations < 4) {
  //       const nextCharCode = str.charCodeAt(i + punctuations);
  //       if (
  //         !(
  //           (nextCharCode >= 32 && nextCharCode <= 47) ||
  //           (nextCharCode >= 58 && nextCharCode <= 64) ||
  //           (nextCharCode >= 91 && nextCharCode <= 96) ||
  //           (nextCharCode >= 123 && nextCharCode <= 126)
  //         )
  //       )
  //         break;
  //       punctuations++;
  //     }
  //     i += punctuations - 1;
  //     tempCount += Math.ceil(punctuations / 2);
  //   } else if (
  //     (charCode >= 65 && charCode <= 90) ||
  //     (charCode >= 97 && charCode <= 122)
  //   ) {
  //     let letters = 1;
  //     while (i + letters < str.length && letters < 2) {
  //       const nextCharCode = str.charCodeAt(i + letters);
  //       if (
  //         !(
  //           (nextCharCode >= 65 && nextCharCode <= 90) ||
  //           (nextCharCode >= 97 && nextCharCode <= 122)
  //         )
  //       )
  //         break;
  //       letters++;
  //     }
  //     i += letters - 1;
  //     tempCount += Math.ceil(letters / 4);
  //   } else {
  //     tempCount++;
  //   }
  // }
  // return tempCount;
}

const doUpdateInsertPosition = (isInit) => {
  const textarea = refLyricTextarea.value
  if (!textarea) return
  stateData.cursorPosition = isInit
    ? renderData.lyric.length
    : textarea.selectionStart

  stateData.beforeText = renderData.lyric.slice(0, stateData.cursorPosition)
  stateData.afterText = renderData.lyric.slice(stateData.cursorPosition)
  // console.log(stateData);
  emits(
    "cursorPositionChange",
    stateData.cursorPosition,
    stateData.beforeText,
    stateData.afterText
  )
}

const handleFocus = () => {
  emits("focus", renderData.lyric)
  setTimeout(() => {
    doUpdateInsertPosition()
  }, 20)
}

const handleCompositionStart = () => {
  renderData.inputing = true
}

const handleCompositionEnd = (e) => {
  renderData.inputing = false
  handleInput(e)
}

const handleInput = (e) => {
  if (!renderData.inputing) {
    e.target.value = e.target.value.slice(0, renderData.maxLength)
    renderData.lyric = e.target.value

    doUpdateInsertPosition()
    emits("input", renderData.lyric)
  }
}

const handleBlur = (e) => {
  emits("blur", renderData.lyric)
}

const handleChange = (e) => {
  emits("change", renderData.lyric)
  // console.log(renderData.maxLength, renderData.lyric.length, e);
}

function cursorMove(inp, spos = -1) {
  // spos 光标的位置 -1为最后一位
  if (spos < 0) spos = inp.value.length
  if (inp.setSelectionRange) {
    //兼容火狐,谷歌
    setTimeout(function () {
      inp.setSelectionRange(spos, spos)
      inp.focus()
    }, 0)
  } else if (inp.createTextRange) {
    //兼容IE
    var rng = inp.createTextRange()
    rng.move("character", spos)
    rng.select()
  }
}
// function cursorMove(inp, spos = -1) {
//   if (spos < 0) spos = inp.value.length;

//   function setCursorPosition(e) {
//     if (inp.setSelectionRange) {
//       inp.focus(); // 先聚焦
//       inp.setSelectionRange(spos, spos);
//     } else if (inp.createTextRange) {
//       var rng = inp.createTextRange();
//       rng.move("character", spos);
//       rng.select();
//     }
//   }

//   inp.addEventListener("focus", setCursorPosition); // 确保 focus 事件已绑定
//   setCursorPosition(); // 或者使用 setTimeout
// }

const doFocusCursorPosition = () => {
  const textarea = refLyricTextarea.value
  cursorMove(textarea, stateData.cursorPosition)
}

watch(
  () => props.lyric,
  (newVal) => {
    if (renderData.lyric != props.lyric) {
      renderData.lyric = props.lyric
      doUpdateInsertPosition(true)
      handleChange()
    }
  }
)

watch(
  () => renderData.lyric,
  (newVal) => {
    emits("input:lyric", newVal)
    renderData.count = calculateChar(renderData.lyric)
    handleChange()
    emits("countChange", renderData.count)
  }
)

watch(
  () => {
    return props.maxLength
  },
  () => {
    renderData.maxLength = props.maxLength ?? MAX_LYRIC_COUNT
  }
)
const blurText = () => {
  refLyricTextarea.value?.setSelectionRange(
    stateData.cursorPosition || 0,
    stateData.cursorPosition || 0
  )
  refLyricTextarea.value?.scrollIntoView({
    behavior: "smooth",
    block: "nearest",
  })
}
const initEvents = () => {
  refLyricTextarea.value?.addEventListener("focus", blurText)
}
onMounted(() => {
  initEvents()
  doUpdateInsertPosition(true)
})

onBeforeUnmount(() => {
  refLyricTextarea.value?.removeEventListener("focus", blurText)
})

defineExpose({
  getBeforeLyrics() {
    return stateData.beforeText
  },
  insertTag(tag) {
    insertTag(tag)
    setTimeout(() => {
      doFocusCursorPosition()
    }, 20)
  },

  insertLyric(lyric, isTemplate) {
    if (isTemplate) {
      if (lyric.length > 0) {
        lyric = lyric.slice(0, renderData.maxLength)
        renderData.lyric = lyric
        stateData.beforeText = renderData.lyric
        stateData.cursorPosition = stateData.beforeText.length
        stateData.afterText = ""
        nextTick(() => {
          emits("change", renderData.lyric)
          emits(
            "cursorPositionChange",
            stateData.cursorPosition,
            stateData.beforeText,
            stateData.afterText
          )
        })
      }
      return
    }
    let contN = 0
    if (lyric.length > 0) {
      if (stateData.beforeText.endsWith("\n")) {
        if (/^\[.+\]/.test(lyric)) {
          if (stateData.beforeText.endsWith("\n\n")) {
            contN = 0
          } else {
            contN = 1
          }
        } else if (!lyric.startsWith("\n")) {
          if (stateData.beforeText.endsWith("\n\n")) {
            contN = 0
          } else if (/\[.+\]\n{0,1}$/.test(stateData.beforeText)) {
            if (stateData.beforeText.endsWith("\n")) {
              contN = 0
            } else {
              contN = 1
            }
          } else {
            if (stateData.beforeText.endsWith("\n")) {
              contN = 0
            } else {
              contN = 1
            }
          }
        } else {
          contN = 0
        }
      } else {
        if (!stateData.beforeText) {
          contN = 0
        } else if (/^\[.+\]/.test(lyric)) {
          if (!lyric.startsWith("\n")) {
            contN = 2
          } else {
            contN = 1
          }
        } else {
          contN = 1
        }
      }
      let beforeText =
        (stateData.beforeText ?? "") +
        Array.from({ length: contN }, (_, i) => "\n").join("")
      const count = beforeText.length + (stateData?.afterText ?? "").length

      if (count < renderData.maxLength) {
        beforeText += lyric.slice(0, renderData.maxLength - count)
        stateData.beforeText = beforeText
        renderData.lyric = stateData.beforeText + stateData.afterText
        stateData.cursorPosition = stateData.beforeText.length
        nextTick(() => {
          emits("change", renderData.lyric)
          emits(
            "cursorPositionChange",
            stateData.cursorPosition,
            stateData.beforeText,
            stateData.afterText
          )
        })
      }
    }
    setTimeout(() => {
      doFocusCursorPosition()
    }, 20)
  },
  initCursorPosition() {
    renderData.maxLength = props.maxLength ?? MAX_LYRIC_COUNT

    stateData.beforeText = renderData.lyric
    stateData.cursorPosition = stateData.beforeText.length
    stateData.afterText = ""
  },
  scrollToBottom() {
    if (!refLyricTextarea.value) return
    refLyricTextarea.value.scrollTop = refLyricTextarea.value?.scrollHeight
  },
})
</script>
<style lang="scss">
.edit-lyric {
  height: 100%;
  position: relative;
  textarea {
    height: 100%;
    width: 100%;
    border: none;
    outline: none;
    resize: none;
    font-family: HarmonyOS Sans SC;
    font-size: 14px;
    font-weight: normal;
    color: #ffffff;
    line-height: 22px;
    background-color: transparent;
    &::placeholder {
      color: rgba(255, 255, 255, 0.5) !important;
    }
  }
}
</style>
